import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import env from "@shared/api/env";
import { supabase } from "@shared/api/supabase";
import type {
  RootInfo,
  HealthInfo,
  FlowSummary,
  FlowDetail,
  RunListItem,
  RunStartRequest,
  RunStartResponse,
  InteractiveStartRequest,
  InteractiveStartResponse,
  StepResponse,
  RecoverRequest,
  BreakpointsAddRequest,
  BreakpointsAddResponse,
  RunStatusResponse,
  RunHistoryResponse,
  SnapshotResponse,
  RunControlResponse,
  RunDeleteResponse,
  BreakpointDeleteResponse,
  RunResumeResponse,
  AutocastSynthesizeRequest,
  AutocastSynthesizeResponse,
  AutocastSynthesizeError,
} from "./blast-api-types";

export class BlastApiClient {
  private client: AxiosInstance;
  private workspaceId: string | null = null;

  constructor(baseURL: string = env.BLAST_API) {
    this.client = axios.create({
      baseURL,
      timeout: 120000,
      headers: { "Content-Type": "application/json" },
    });
    // Inject workspace context into every Axios request
    this.client.interceptors.request.use(async (config) => {
      // Ensure headers object exists and is plain-assignable
      config.headers = (config.headers || {}) as any;

      // Workspace context
      if (this.workspaceId) {
        const ws = this.workspaceId;
        (config.headers as any)["X-Workspace-ID"] = ws;
        config.params = { ...(config.params || {}), workspaceId: ws } as any;
      }

      // Authorization via Supabase session (short timeout fallback)
      try {
        const result = (await Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve(undefined), 1500)),
        ])) as any;
        const access = result?.data?.session?.access_token as string | undefined;
        if (access) {
          (config.headers as any).Authorization = `Bearer ${access}`;
        }
      } catch {
        // Ignore auth fetch errors; proceed without Authorization
      }
      return config;
    });

    // Normalize error messages from backend to surface meaningful toasts
    this.client.interceptors.response.use(
      (res) => res,
      (err) => {
        try {
          const resp = err?.response;
          const data = resp?.data;
          const serverMsg =
            (typeof data?.error?.message === 'string' && data.error.message) ||
            (typeof data?.message === 'string' && data.message) ||
            (typeof data === 'string' ? data : undefined);
          const code = typeof data?.error?.code === 'string' ? data.error.code : (typeof data?.code === 'string' ? data.code : undefined);
          if (serverMsg) {
            const msg = code ? `${serverMsg} (${code})` : serverMsg;
            err.message = msg;
            (err as any).friendlyMessage = msg;
          }
          // Capture detailed server-provided explanation if present
          const details = data?.details ?? data?.error?.details;
          const detailsStr = typeof details?.error === 'string'
            ? details.error
            : (typeof details === 'string'
              ? details
              : (typeof data?.error?.details?.error === 'string'
                ? data.error.details.error
                : (typeof data?.error?.details === 'string'
                  ? data.error.details
                  : (typeof data?.error === 'string' ? data.error : undefined))));
          if (detailsStr) {
            (err as any).friendlyDetails = detailsStr;
          }
        } catch {
          // ignore, leave default message
        }
        return Promise.reject(err);
      },
    );
  }

  async getServiceInfo(): Promise<RootInfo> {
    const response: AxiosResponse<RootInfo> = await this.client.get("/");
    return response.data;
  }
  async getHealth(): Promise<HealthInfo> {
    const response: AxiosResponse<HealthInfo> =
      await this.client.get("/health");
    return response.data;
  }
  async listFlows(workspaceId?: string): Promise<{ items: FlowSummary[] }> {
    const response: AxiosResponse<{ items: FlowSummary[] }> = await this.client.get(
      "/flows",
      workspaceId
        ? {
            params: { workspaceId },
            headers: { "X-Workspace-ID": workspaceId },
          }
        : undefined,
    );
    return response.data;
  }
  async getFlowById(id: string, includeGraph?: boolean): Promise<FlowDetail> {
    const params = includeGraph ? { include: "graph" } : {};
    const encodedId = encodeURIComponent(id);
    const response: AxiosResponse<FlowDetail> = await this.client.get(
      `/flows/${encodedId}`,
      { params },
    );
    return response.data;
  }
  async listRuns(): Promise<{ items: RunListItem[] }> {
    const response: AxiosResponse<{ items: RunListItem[] }> =
      await this.client.get("/runs");
    return response.data;
  }
  async startRun(request: RunStartRequest): Promise<RunStartResponse> {
    const response: AxiosResponse<RunStartResponse> = await this.client.post(
      "/runs",
      request,
    );
    return response.data;
  }
  async startInteractiveRun(
    request: InteractiveStartRequest,
  ): Promise<InteractiveStartResponse> {
    const response: AxiosResponse<InteractiveStartResponse> =
      await this.client.post("/runs/interactive", request);
    return response.data;
  }
  async stepRun(runId: string): Promise<StepResponse> {
    const response: AxiosResponse<StepResponse> = await this.client.post(
      `/runs/${runId}/step`,
    );
    return response.data;
  }
  async resumeRun(runId: string): Promise<RunControlResponse> {
    const response: AxiosResponse<RunControlResponse> = await this.client.post(
      `/runs/${runId}/resume`,
    );
    return response.data;
  }
  async resumeRunSkip(runId: string): Promise<RunResumeResponse> {
    const response: AxiosResponse<RunResumeResponse> = await this.client.post(
      `/runs/${runId}/resume`,
      undefined,
      { params: { skipCurrent: 1 } },
    );
    return response.data;
  }
  async pauseRun(runId: string): Promise<RunControlResponse> {
    const response: AxiosResponse<RunControlResponse> = await this.client.post(
      `/runs/${runId}/pause`,
    );
    return response.data;
  }
  async recoverRun(
    runId: string,
    request: RecoverRequest,
  ): Promise<RunControlResponse> {
    const response: AxiosResponse<RunControlResponse> = await this.client.post(
      `/runs/${runId}/recover`,
      request,
    );
    return response.data;
  }
  async deliverRun(
    runId: string,
    correlationId: string,
    payload?: Record<string, any>,
    flowId?: string,
    persist?: { rootDir?: string },
  ): Promise<{ runId: string; delivered: string; status: string }> {
    const response = await this.client.post(`/runs/${runId}/deliver`, {
      correlationId,
      payload: payload || {},
      flowId,
      persist,
    });
    return response.data;
  }
  async addBreakpoints(
    runId: string,
    request: BreakpointsAddRequest,
  ): Promise<BreakpointsAddResponse> {
    const response: AxiosResponse<BreakpointsAddResponse> =
      await this.client.post(`/runs/${runId}/breakpoints`, request);
    return response.data;
  }
  async removeBreakpoint(
    runId: string,
    nodeId: string,
  ): Promise<BreakpointDeleteResponse> {
    const encodedNodeId = encodeURIComponent(nodeId);
    const response: AxiosResponse<BreakpointDeleteResponse> =
      await this.client.delete(`/runs/${runId}/breakpoints/${encodedNodeId}`);
    return response.data;
  }
  async getRunStatus(runId: string): Promise<RunStatusResponse> {
    const response: AxiosResponse<RunStatusResponse> = await this.client.get(
      `/runs/${runId}/status`,
    );
    return response.data;
  }
  async getRunHistory(
    runId: string,
    offset?: number,
    limit?: number,
  ): Promise<RunHistoryResponse> {
    const params: Record<string, any> = {};
    if (offset !== undefined) params.offset = offset;
    if (limit !== undefined) params.limit = limit;
    const response: AxiosResponse<RunHistoryResponse> = await this.client.get(
      `/runs/${runId}/history`,
      { params },
    );
    return response.data;
  }
  async getRunSnapshot(runId: string): Promise<SnapshotResponse> {
    const response: AxiosResponse<SnapshotResponse> = await this.client.get(
      `/runs/${runId}/snapshot`,
    );
    return response.data;
  }
  async deleteRun(runId: string): Promise<RunDeleteResponse> {
    const response: AxiosResponse<RunDeleteResponse> = await this.client.delete(
      `/runs/${runId}`,
    );
    return response.data;
  }
  async synthesizeAutocast(
    request: AutocastSynthesizeRequest,
  ): Promise<AutocastSynthesizeResponse> {
    const response: AxiosResponse<
      AutocastSynthesizeResponse | AutocastSynthesizeError
    > = await this.client.post("/autocast/synthetise", request, {
      baseURL: env.CONDUIT_BASE || this.client.defaults.baseURL,
    });
    const data = response.data;
    const maybeError = (data as AutocastSynthesizeError | undefined)?.error;
    if (maybeError && typeof maybeError === "object") {
      const message =
        (typeof maybeError.message === "string" && maybeError.message) ||
        "Autocast synthesis failed";
      const err = new Error(message);
      (err as any).friendlyMessage = message;
      if (maybeError.details) {
        (err as any).friendlyDetails = maybeError.details;
      }
      (err as any).payload = data;
      throw err;
    }
    return data as AutocastSynthesizeResponse;
  }
  createEventSource(
    runId: string,
    opts: { replay?: "all" | "missed"; since?: number | string } = {},
  ): EventSource {
    const encodedRunId = encodeURIComponent(runId);
    const replay = opts.replay ?? "missed";
    const params: string[] = [`replay=${replay}`];
    if (typeof opts.since !== 'undefined') params.push(`since=${encodeURIComponent(String(opts.since))}`);
    if (this.workspaceId) params.push(`workspaceId=${encodeURIComponent(this.workspaceId)}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return new EventSource(
      `${this.client.defaults.baseURL}/runs/${encodedRunId}/events${qs}`,
    );
  }
  createSnapshotEventSource(
    runId: string,
    opts: { replay?: "all" | "missed"; interval?: number } = {},
  ): EventSource {
    const encodedRunId = encodeURIComponent(runId);
    const replay = opts.replay ?? "missed";
    const params: string[] = [`replay=${replay}`];
    if (opts.interval) params.push(`interval=${opts.interval}`);
    if (this.workspaceId) params.push(`workspaceId=${encodeURIComponent(this.workspaceId)}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return new EventSource(
      `${this.client.defaults.baseURL}/runs/${encodedRunId}/snapshots/stream${qs}`,
    );
  }
  setWorkspace(workspaceId: string | null): void {
    this.workspaceId = workspaceId;
  }
  updateBaseURL(baseURL: string): void {
    this.client.defaults.baseURL = baseURL;
  }
  setTimeout(timeout: number): void {
    this.client.defaults.timeout = timeout;
  }
}

export const blastApi = new BlastApiClient();
export default blastApi;
